import { createContext, useCallback, useContext, useEffect, useState } from "react";
import client, { clearToken, formatApiErrorDetail, refreshSession, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [error, setError] = useState("");

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch (e) {
      try {
        const u = await refreshSession();
        setUser(u);
      } catch (e2) {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await client.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await client.post("/auth/register", payload);
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await client.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing the local session anyway", e);
    }
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
